// Hook to fetch and manage experts list

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { expertAPI } from '../api/expert.api';
import type { ExpertFilters } from '../types/expert.types';

export const useExperts = () => {
  const [filters, setFilters] = useState<ExpertFilters>({
    searchQuery: '',
    sortBy: 'dossiers',
    sortOrder: 'desc',
    activeFilter: 'all'
  });

  // Fetch all experts
  const {
    data: expertsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['admin-experts'],
    queryFn: expertAPI.getAllExperts,
    staleTime: 60 * 1000, // 1 minute
  });

  // Process and filter experts
  const processedExperts = useMemo(() => {
    if (!expertsData?.data?.experts) return [];

    const filtered = expertsData.data.experts.filter(expert => {
      // Apply search filter
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch = 
          expert.nom_traitant?.toLowerCase().includes(query) ||
          expert.fm_name?.toLowerCase().includes(query) ||
          expert.email?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Apply status filter
      if (filters.activeFilter !== 'all') {
        // Calculate combined workload score
        const workloadScore = (expert.stats.active_dossiers * 0.5) + 
                             ((expert.stats.next_week_appointments + expert.stats.week_after_appointments) * 0.5);
        
        switch (filters.activeFilter) {
          case 'highWorkload':
            // Experts with combined workload score > 10
            return workloadScore > 10;
          case 'available':
            // Experts with combined workload score < 5
            return workloadScore < 5;
          default:
            return true;
        }
      }
      
      return true;
    });

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'name':
          comparison = a.nom_traitant.localeCompare(b.nom_traitant);
          break;
        case 'dossiers': {
          // Sort by combined workload (50% dossiers, 50% upcoming appointments)
          const workloadA = (a.stats.active_dossiers * 0.5) +
                           ((a.stats.next_week_appointments + a.stats.week_after_appointments) * 0.5);
          const workloadB = (b.stats.active_dossiers * 0.5) +
                           ((b.stats.next_week_appointments + b.stats.week_after_appointments) * 0.5);
          comparison = workloadA - workloadB;
          break;
        }
        case 'nextWeek':
          comparison = a.stats.next_week_appointments - b.stats.next_week_appointments;
          break;
        case 'weekAfter':
          comparison = a.stats.week_after_appointments - b.stats.week_after_appointments;
          break;
        default: {
          // Default to sorting by combined workload descending
          const defaultWorkloadA = (a.stats.active_dossiers * 0.5) +
                                  ((a.stats.next_week_appointments + a.stats.week_after_appointments) * 0.5);
          const defaultWorkloadB = (b.stats.active_dossiers * 0.5) +
                                  ((b.stats.next_week_appointments + b.stats.week_after_appointments) * 0.5);
          comparison = defaultWorkloadB - defaultWorkloadA;
          return comparison;
        }
      }
      
      // Apply sort order
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [expertsData, filters]);

  return {
    experts: processedExperts,
    totalExperts: expertsData?.data?.total || 0,
    isLoading,
    error,
    filters,
    setFilters,
    updateFilter: (key: keyof ExpertFilters, value: any) => {
      setFilters(prev => ({ ...prev, [key]: value }));
    },
    refetch
  };
};